import { cloneDeep } from 'lodash'
import {
  COMPONENT,
  LIST,
  resizingOptions,
  responsivePositioningOptions,
} from '@adalo/constants'
import { update } from '@adalo/utils'
import {
  ContainerAttributes,
  DeviceValue,
  DeviceVisibility,
  EditorObject,
  LayoutAttributes,
} from 'utils/responsiveTypes'
import getDeviceObject from 'utils/getDeviceObject'
import { ObjectList } from '../types/ObjectList'
import { ObjectPathMap } from '../types/ObjectPathMap'
import getObject from '../objects/getObject'
import { NON_ZERO_WIDTH } from '../model/ObjectNode'
import { ConditionResolver } from '../device-layouts/conditions'
import updateDeviceLayoutForObject from '../device-layouts'
import { getParent } from '../device-layouts/utils'
import DeviceType from '../types/DeviceType'
import { DEFAULT_RENDER_DEVICES } from './mapDocumentToState'
import getCustomListProperties, {
  COLUMN_BREAKPOINTS,
} from '../objects/getCustomListProperties'

const { LEFT, CENTER, LEFT_AND_RIGHT } = responsivePositioningOptions
const { SCALES_WITH_PARENT, FIXED } = resizingOptions

// WARNING!!!!
// Do NOT use these functions for any part of the product where fluid motion is required

// NOTE
// These functions...
// - WILL re-calculate the left and right values for the object based on the parent container
// - WILL NOT change the width of any object
// - WILL NOT change the width of the parent container

const getContainerWidthAndOffsetX = (
  container: EditorObject
): {
  containerWidth: number
  containerOffsetX: number
} => {
  const containerWidth =
    container.width === 0 ? NON_ZERO_WIDTH : container.width

  if (container.type === COMPONENT) {
    return {
      containerWidth,
      containerOffsetX: 0,
    }
  }

  if (container.type === LIST && container.rowMargin && container.columnCount) {
    const columnCount = container.columnCount ?? 1

    return {
      containerWidth:
        (containerWidth - container.rowMargin * (columnCount - 1)) /
        columnCount,
      containerOffsetX: container.x,
    }
  }

  return {
    containerWidth,
    containerOffsetX: container.x,
  }
}

const getParentDeviceObject = (
  parent: EditorObject,
  device: DeviceValue | undefined
): EditorObject => {
  const parentDevice = getDeviceObject(parent, device)

  if (
    parentDevice.type === LIST &&
    parentDevice.columnBreakpoints === COLUMN_BREAKPOINTS.CUSTOM
  ) {
    const customListProperties = getCustomListProperties(parentDevice, device)

    return {
      ...parentDevice,
      ...customListProperties,
    }
  }

  return parentDevice
}

const computeLeftRight = (
  parent: EditorObject,
  child: EditorObject,
  device: DeviceValue | undefined
): Partial<LayoutAttributes & ContainerAttributes & DeviceVisibility> => {
  const parentDevice = getParentDeviceObject(parent, device)
  const childDevice = getDeviceObject(child, device)

  const { containerWidth, containerOffsetX } =
    getContainerWidthAndOffsetX(parentDevice)

  const { responsivity = {} } = childDevice
  const {
    horizontalScaling: scaling = FIXED,
    horizontalPositioning: positioning = scaling === FIXED ? LEFT : CENTER,
  } = responsivity

  const leftOffset = childDevice.x - containerOffsetX
  const width = childDevice.width
  const rightOffset = containerWidth - (leftOffset + width)

  const changes = {} as Partial<
    LayoutAttributes & ContainerAttributes & DeviceVisibility
  >

  if (scaling === SCALES_WITH_PARENT && positioning === CENTER) {
    const leftRatio = containerWidth === 0 ? 0 : leftOffset / containerWidth
    const rightRatio = containerWidth === 0 ? 0 : rightOffset / containerWidth

    changes.left = leftRatio
    changes.right = rightRatio
  } else if (scaling === SCALES_WITH_PARENT && positioning === LEFT_AND_RIGHT) {
    changes.left = leftOffset
    changes.right = rightOffset
  }

  return changes
}

export const computeLeftRightForObject = (
  objectList: ObjectList,
  pathMap: ObjectPathMap,
  parent: EditorObject,
  child: EditorObject,
  renderDevices: (DeviceType | undefined)[]
): ObjectList => {
  let updatedList = objectList

  for (const device of renderDevices) {
    if (device === undefined) {
      continue
    }

    const deviceObject = getDeviceObject(child, device)

    if (
      (deviceObject.minWidthEnabled && deviceObject.minWidthEnabled === true) ||
      (deviceObject.maxWidthEnabled && deviceObject.maxWidthEnabled === true)
    ) {
      const resolver = new ConditionResolver(device)
      updatedList = updateDeviceLayoutForObject(
        updatedList,
        pathMap,
        child.id,
        device,
        resolver,
        computeLeftRight(parent, child, device)
      )
    }
  }

  if (renderDevices.includes(undefined)) {
    const changes = computeLeftRight(parent, child, undefined)
    const updatedChild = getObject(updatedList, pathMap, child.id)

    if (
      (updatedChild.minWidthEnabled && updatedChild.minWidthEnabled === true) ||
      (updatedChild.maxWidthEnabled && updatedChild.maxWidthEnabled === true)
    ) {
      updatedList = update(updatedList, pathMap[child.id], {
        ...updatedChild,
        ...changes,
      }) as ObjectList
    }
  }

  return updatedList
}

export const refreshLeftRightForObjectChildren = (
  objectList: ObjectList,
  pathMap: ObjectPathMap,
  objectId: string,
  renderDevices: (DeviceType | undefined)[] = DEFAULT_RENDER_DEVICES
): ObjectList => {
  const group = getObject(objectList, pathMap, objectId)

  if (
    group.children &&
    Array.isArray(group.children) &&
    group.children.length > 0
  ) {
    let updatedList = cloneDeep(objectList)

    for (const child of group.children) {
      updatedList = computeLeftRightForObject(
        updatedList,
        pathMap,
        group,
        child,
        renderDevices
      )
    }

    return updatedList
  }

  return objectList
}

export const refreshLeftRightForObjects = (
  objectList: ObjectList,
  pathMap: ObjectPathMap,
  objectIds: string[],
  renderDevices: (DeviceType | undefined)[] = DEFAULT_RENDER_DEVICES
): ObjectList => {
  if (Array.isArray(objectIds) && objectIds.length === 0) {
    return objectList
  }

  let updatedList = cloneDeep(objectList)

  for (const objectId of objectIds) {
    const object = getObject(updatedList, pathMap, objectId)
    const parent = getParent(updatedList, pathMap, object.id)

    updatedList = computeLeftRightForObject(
      updatedList,
      pathMap,
      parent,
      object,
      renderDevices
    )
  }

  return updatedList
}
